import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";

type OAuthAuthClient = { name?: string; client_name?: string; redirect_uri?: string };
type OAuthAuthDetails = {
  client?: OAuthAuthClient | null;
  scope?: string | null;
  redirect_url?: string | null;
  redirect_to?: string | null;
};
type OAuthResult = { data: OAuthAuthDetails | null; error: { message: string } | null };
type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<OAuthResult>;
  approveAuthorization: (id: string) => Promise<OAuthResult>;
  denyAuthorization: (id: string) => Promise<OAuthResult>;
};
const oauthApi = () => (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s['authorization_id'] === "string" ? s['authorization_id'] : "",
  }),
  head: () => ({
    meta: [
      { title: "Authorize access — USA Lottery Club" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Consent,

});

function Consent() {
  const { authorization_id: authorizationId } = Route.useSearch();
  const [status, setStatus] = useState<"loading" | "signin" | "ready" | "error">("loading");
  const [details, setDetails] = useState<OAuthAuthDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authorizationId) {
        setError("Missing authorization_id in the request URL.");
        setStatus("error");
        return;
      }
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        if (!cancelled) setStatus("signin");
        return;
      }
      setEmail(sessionData.session.user.email ?? null);
      const { data, error: err } = await oauthApi().getAuthorizationDetails(authorizationId);
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setStatus("error");
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
      setStatus("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const api = oauthApi();
    const { data, error: err } = approve
      ? await api.approveAuthorization(authorizationId)
      : await api.denyAuthorization(authorizationId);
    if (err) {
      setBusy(false);
      setError(err.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  async function signIn() {
    await lovable.auth.signInWithOAuth("lovable", { redirect_uri: window.location.href });
  }

  const clientName = details?.client?.name ?? details?.client?.client_name ?? "this client";

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-12">
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-foreground">
          {status === "signin" ? "Sign in to continue" : `Connect ${clientName} to USA Lottery Club`}
        </h1>

        {status === "loading" && <p className="mt-3 text-sm text-muted-foreground">Loading authorization request…</p>}

        {status === "signin" && (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Sign in to your account to approve this connection request.
            </p>
            <Button className="mt-5 w-full" onClick={signIn}>
              Sign in with Lovable
            </Button>
          </>
        )}

        {status === "ready" && (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              This lets {clientName} use USA Lottery Club tools as you.
            </p>
            <dl className="mt-4 space-y-2 text-sm">
              {email && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Signed in as</dt>
                  <dd className="text-foreground">{email}</dd>
                </div>
              )}
              {details?.client?.redirect_uri && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Redirects to</dt>
                  <dd className="break-all text-right text-foreground">{details.client.redirect_uri}</dd>
                </div>
              )}
              {details?.scope && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Requested access</dt>
                  <dd className="text-right text-foreground">{details.scope}</dd>
                </div>
              )}
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">
              This does not bypass this app's permissions or backend policies.
            </p>
            <div className="mt-6 flex gap-3">
              <Button className="flex-1" disabled={busy} onClick={() => decide(true)}>
                Approve
              </Button>
              <Button className="flex-1" variant="outline" disabled={busy} onClick={() => decide(false)}>
                Cancel connection
              </Button>
            </div>
          </>
        )}

        {error && (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </main>
  );
}
